<template>
  <button
    v-if="cultures?.length > 1"
    v-tooltip.left="'Toggle culture (' + currentCulture + ')'"
    @click="switchCulture">
    <a-icon :icon="cultureFlagIcon" />
  </button>
</template>

<script setup lang="ts">
import type { Ref } from "vue";
import type { CultureDto } from "../../types";
import {
  computed,
  ref,
  watch,
  useCurrentCulture,
  useHeadlessDataState,
  useHeadlessCulturesResource,
} from "#imports";

const { listCultures } = useHeadlessCulturesResource();
const currentCulture: Ref<string | null> = useCurrentCulture();

const headlessData = useHeadlessDataState();

const cultureList: Ref<CultureDto[] | null> = ref( null );
if ( import.meta.dev ) {
  const res = await listCultures();
  cultureList.value = res.data.value;
}

const cultures = computed( () => {
  const allCultures = Array.isArray( cultureList.value )
    ? cultureList.value?.map( c => c.culture?.toLowerCase() || "" )
    : [];

  if ( headlessData.value && headlessData.value.type === "content" ) {
    const available = headlessData.value.data.availableCultures;
    if ( Array.isArray( available ) ) {
      return available;
    }
  }

  return allCultures;
} );

const selectedCultureIndex = computed( {
  get () {
    return cultures.value?.indexOf( ( currentCulture.value ?? "" ).toLowerCase() ) ?? -1;
  },
  set ( index ) {
    if ( currentCulture.value ) {
      currentCulture.value = cultures.value[index].toLowerCase();
    }
  },
} );

const cultureFlagIcon = ref( "circle-flags:xx" );

const cultureFlagMap: Record<string, string> = {
  "en": "earth",
  "en-001": "earth",
};

watch( selectedCultureIndex, ( val ) => {
  let result: string = val === -1 ? "xx" : currentCulture.value ?? "xx";

  if ( result.includes( "-" ) ) {
    const split = result.split( "-" );
    if ( split && split.length > 1 ) {
      result = split[1];
    }
  }

  const mapping = cultureFlagMap[result.toLowerCase()];

  cultureFlagIcon.value = `circle-flags:${mapping ?? result.toLowerCase()}`;
}, { immediate: true } );

function switchCulture () {
  if ( selectedCultureIndex.value + 1 > cultures.value.length - 1 ) {
    selectedCultureIndex.value = 0;
  } else {
    selectedCultureIndex.value = selectedCultureIndex.value + 1;
  }
}
</script>

<style scoped>

</style>
